// ignore-string-externalization
//
import React, { useContext, MutableRefObject } from 'react';
import {
  useAnimations,
  customThreshold,
  GraphicTransitionContext,
  MODULE_COMPONENTS as FRODOR_MODULES_COMPONENTS,
  AnimationContext,
} from '@spotify-internal/frodor-modules';
import { FAQLinksModule } from './FAQLinksModule';
import { JumpLinkHeroModule } from './JumpLinkHeroModule';
import { LinkGridModule } from './LinkGridModule';
import { RelatedStoriesListModule } from './RelatedStoriesListModule';
import { ArticleHeaderModule } from './ArticleHeaderModule';
import { ArticleModule } from './ArticleModule';
import { FeaturedContentModule } from './FeaturedContentModule';
import { ModuleContentType, ModuleType } from './types';
import { TrackVisibility } from '../../utils/Visibility/TrackVisibility';

export const Module = React.forwardRef<
  MutableRefObject<ModuleType>,
  Omit<ModuleType, 'ref'>
>((props, ref) => {
  const {
    contentType,
    shouldAnimate = true,
    title = '',
    onShowSlide,
    modulePageIndex,
  } = props;
  const ModuleComponent = MODULE_COMPONENTS[contentType];
  const { animationContext, animationRef } = useAnimations(
    contentType,
    shouldAnimate,
    customThreshold(contentType),
  );

  const { getTransitionColors, showCarouselSlide } = useContext(
    GraphicTransitionContext,
  );

  const showSlideWithContext = (slide: ModuleType, idx: number) => {
    showCarouselSlide && showCarouselSlide(props as ModuleType, slide);
    onShowSlide && onShowSlide(slide, idx);
  };
  const isTransitionModule = contentType === 'graphicTransitionModule';
  // include graphic transition context / colors
  const modProps = {
    ...props,
    onShowSlide: showSlideWithContext,
    transitionColors:
      isTransitionModule &&
      modulePageIndex &&
      getTransitionColors &&
      getTransitionColors(modulePageIndex),
  };
  if (ModuleComponent) {
    const longModuleThresholds: { [key: string]: number } = {
      linkGrid: 0.4,
      accordionModule: 0.4,
      articleModule: 0.01,
    };
    return (
      <TrackVisibility
        threshold={longModuleThresholds[contentType] || 0.6}
        eventCategory={`Frodor - module-${contentType}`}
        eventLabel={title}
      >
        <AnimationContext.Provider value={animationContext}>
          <div
            id={`${props.id}-${props.contentType}`}
            style={{ scrollMarginTop: '76px' }}
          >
            <ModuleComponent {...modProps} ref={mergeRefs(ref, animationRef)} />
          </div>
        </AnimationContext.Provider>
      </TrackVisibility>
    );
  }
  return <>Module type "{contentType}" not found</>;
});

const MODULE_COMPONENTS: {
  [key in ModuleContentType]: React.FC<any> | undefined;
} = {
  ...FRODOR_MODULES_COMPONENTS,
  // These modules are overrides to the Frodor modules to add additional functionality or pass additional props
  articleModule: ArticleModule,
  linkGrid: LinkGridModule,
  faqLinksModule: FAQLinksModule,
  jumpLinkHeroModule: JumpLinkHeroModule,
  relatedStoriesList: RelatedStoriesListModule,
  featuredContentModule: FeaturedContentModule,
  articleHeaderModule: ArticleHeaderModule,
};

const mergeRefs = (
  ...refs: Array<React.MutableRefObject<any> | Function | null>
) => {
  const filteredRefs: Array<React.MutableRefObject<any> | Function | null> =
    refs.filter(Boolean);
  if (!filteredRefs.length) return null;
  if (filteredRefs.length === 1) return filteredRefs[0];
  return (inst: any) => {
    for (const elementRef of filteredRefs) {
      if (typeof elementRef === 'function') {
        elementRef(inst);
      } else if (elementRef) {
        elementRef.current = inst;
      }
    }
  };
};
